import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getClients, 
  getWalletDashboardStats, 
  getLowBalanceAlerts,
  getWalletTransactions,
  topupWallet
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Wallet, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  Plus, 
  ArrowUpRight, 
  ArrowDownRight,
  Download,
  Filter,
  Users,
  Receipt,
  RefreshCw,
  Eye,
  Building2,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/lib/utils';
import SmartSearch from '@/components/SmartSearch';

// Column definitions for wallet balances search
const walletColumns = [
  { key: 'display_name', label: 'Client', type: 'text' },
  { key: 'client_code', label: 'Code', type: 'text' },
  { key: 'client_type', label: 'Type', type: 'select', options: [
    { value: 'Company', label: 'Company' },
    { value: 'Individual', label: 'Individual' }
  ]},
  { key: 'wallet_balance', label: 'Balance', type: 'number' },
  { key: 'emirate', label: 'Emirate', type: 'text' },
];

// Quick filters for wallets
const walletQuickFilters = [
  { label: 'Has Balance', filters: { wallet_balance: '>0' } },
  { label: 'Zero Balance', filters: { wallet_balance: '0' } },
  { label: 'Companies', filters: { client_type: 'Company' } },
  { label: 'Individuals', filters: { client_type: 'Individual' } },
];

// Column definitions for transactions search
const transactionColumns = [
  { key: 'transaction_number', label: 'Transaction #', type: 'text' },
  { key: 'client_name', label: 'Client', type: 'text' },
  { key: 'type', label: 'Type', type: 'select', options: [
    { value: 'topup', label: 'Top Up' },
    { value: 'payment', label: 'Payment' },
    { value: 'refund', label: 'Refund' },
  ]},
  { key: 'amount', label: 'Amount', type: 'number' },
  { key: 'payment_method', label: 'Method', type: 'text' },
  { key: 'reference_number', label: 'Reference', type: 'text' },
  { key: 'created_at', label: 'Date', type: 'date' },
];

// Quick filters for transactions
const transactionQuickFilters = [
  { label: 'Top Ups', filters: { type: 'topup' } },
  { label: 'Payments', filters: { type: 'payment' } },
  { label: 'Refunds', filters: { type: 'refund' } },
  { label: 'Today', filters: { created_at: 'today' } },
  { label: 'This Month', filters: { created_at: 'thisMonth' } },
];

export default function WalletReportsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Stats
  const [walletStats, setWalletStats] = useState(null);
  const [lowBalanceAlerts, setLowBalanceAlerts] = useState([]);
  
  // Client wallets
  const [allClients, setAllClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  
  // Transactions
  const [allTransactions, setAllTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  
  // Top-up dialog
  const [topupDialogOpen, setTopupDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [topupForm, setTopupForm] = useState({
    amount: '',
    payment_method: 'Cash',
    reference_number: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, alertsRes, clientsRes] = await Promise.all([
        getWalletDashboardStats().catch(() => ({ data: null })),
        getLowBalanceAlerts().catch(() => ({ data: [] })),
        getClients({ limit: 1000 }),
      ]);
      
      setWalletStats(statsRes.data);
      setLowBalanceAlerts(alertsRes.data || []);
      
      // Filter clients with wallet data
      const clientsWithWallet = clientsRes.data.items || [];
      setAllClients(clientsWithWallet);
      setFilteredClients(clientsWithWallet);
      
      // Load all transactions from all clients
      await loadAllTransactions(clientsWithWallet);
      
    } catch (error) {
      toast.error('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const loadAllTransactions = async (clients) => {
    try {
      // Get transactions for clients with wallet balance
      const clientsWithBalance = clients.filter(c => (c.wallet_balance || 0) > 0);
      const transactionPromises = clientsWithBalance.slice(0, 20).map(client => 
        getWalletTransactions(client.id, { limit: 100 }).catch(() => ({ data: { items: [] } }))
      );
      
      const results = await Promise.all(transactionPromises);
      const allTx = results.flatMap((res, idx) => {
        const items = res.data?.items || res.data || [];
        return items.map(tx => ({
          ...tx,
          client_name: clientsWithBalance[idx]?.display_name,
          client_id: clientsWithBalance[idx]?.id
        }));
      });
      
      // Sort by date descending
      allTx.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setAllTransactions(allTx);
      setFilteredTransactions(allTx);
    } catch (error) {
      console.error('Failed to load transactions', error);
    }
  };

  const handleTopup = async () => {
    if (!selectedClient || !topupForm.amount || parseFloat(topupForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setSaving(true);
    try {
      await topupWallet(selectedClient.id, {
        amount: parseFloat(topupForm.amount),
        payment_method: topupForm.payment_method,
        reference_number: topupForm.reference_number || null,
        notes: topupForm.notes || null
      });
      
      toast.success(`Wallet topped up with ${formatCurrency(topupForm.amount)}`);
      setTopupDialogOpen(false);
      setSelectedClient(null);
      setTopupForm({ amount: '', payment_method: 'Cash', reference_number: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to top up wallet');
    } finally {
      setSaving(false);
    }
  };

  const openTopupDialog = (client) => {
    setSelectedClient(client);
    setTopupDialogOpen(true);
  };

  const getTransactionTypeIcon = (type) => {
    switch (type) {
      case 'topup':
        return <ArrowDownRight className="w-4 h-4 text-green-500" />;
      case 'payment':
        return <ArrowUpRight className="w-4 h-4 text-red-500" />;
      case 'refund':
        return <RefreshCw className="w-4 h-4 text-blue-500" />;
      default:
        return <Receipt className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getTransactionTypeBadge = (type) => {
    switch (type) {
      case 'topup':
        return <Badge className="bg-green-100 text-green-700">Top Up</Badge>;
      case 'payment':
        return <Badge className="bg-red-100 text-red-700">Payment</Badge>;
      case 'refund':
        return <Badge className="bg-blue-100 text-blue-700">Refund</Badge>;
      default:
        return <Badge variant="secondary">{type}</Badge>;
    }
  };

  // Calculate summary stats
  const totalWalletBalance = allClients.reduce((sum, c) => sum + (c.wallet_balance || 0), 0);
  const clientsWithBalance = allClients.filter(c => (c.wallet_balance || 0) > 0).length;
  const topupsThisMonth = allTransactions.filter(tx => {
    const txDate = new Date(tx.created_at);
    const now = new Date();
    return tx.type === 'topup' && 
           txDate.getMonth() === now.getMonth() && 
           txDate.getFullYear() === now.getFullYear();
  });
  const topupAmountThisMonth = topupsThisMonth.reduce((sum, tx) => sum + (tx.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="wallet-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="w-6 h-6 text-primary" />
            Wallet Reports
          </h1>
          <p className="text-muted-foreground">Manage client wallets, top-ups, and transactions</p>
        </div>
        <Button onClick={() => setTopupDialogOpen(true)} data-testid="quick-topup-btn">
          <Plus className="w-4 h-4 mr-2" />
          Quick Top Up
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Wallet Balance</p>
                <p className="text-2xl font-bold">{formatCurrency(totalWalletBalance)}</p>
              </div>
              <div className="p-3 rounded-full bg-primary/10">
                <Wallet className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Clients with Balance</p>
                <p className="text-2xl font-bold">{clientsWithBalance}</p>
                <p className="text-xs text-muted-foreground">of {allClients.length} total</p>
              </div>
              <div className="p-3 rounded-full bg-green-100">
                <Users className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Top-ups This Month</p>
                <p className="text-2xl font-bold">{formatCurrency(topupAmountThisMonth)}</p>
                <p className="text-xs text-muted-foreground">{topupsThisMonth.length} transactions</p>
              </div>
              <div className="p-3 rounded-full bg-blue-100">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Low Balance Alerts</p>
                <p className="text-2xl font-bold">{lowBalanceAlerts.length}</p>
                <p className="text-xs text-muted-foreground">Need attention</p>
              </div>
              <div className={`p-3 rounded-full ${lowBalanceAlerts.length > 0 ? 'bg-amber-100' : 'bg-gray-100'}`}>
                <AlertTriangle className={`w-6 h-6 ${lowBalanceAlerts.length > 0 ? 'text-amber-600' : 'text-gray-400'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="balances">Client Balances</TabsTrigger>
          <TabsTrigger value="transactions">All Transactions</TabsTrigger>
          <TabsTrigger value="topups">Top-up History</TabsTrigger>
          <TabsTrigger value="alerts">Low Balance Alerts</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Wallet Balances */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Wallet Balances</CardTitle>
                <CardDescription>Clients with highest wallet balances</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allClients
                      .filter(c => (c.wallet_balance || 0) > 0)
                      .sort((a, b) => (b.wallet_balance || 0) - (a.wallet_balance || 0))
                      .slice(0, 10)
                      .map(client => (
                        <TableRow key={client.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {client.client_type === 'Company' ? (
                                <Building2 className="w-4 h-4 text-primary" />
                              ) : (
                                <User className="w-4 h-4 text-muted-foreground" />
                              )}
                              <span className="font-medium">{client.display_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono font-semibold text-green-600">
                            {formatCurrency(client.wallet_balance)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => openTopupDialog(client)}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Top Up
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    {allClients.filter(c => (c.wallet_balance || 0) > 0).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                          No clients with wallet balance
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Recent Transactions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Transactions</CardTitle>
                <CardDescription>Latest wallet activity</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allTransactions.slice(0, 10).map(tx => (
                      <TableRow key={tx.id || tx.transaction_number}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getTransactionTypeIcon(tx.type)}
                            <span className="capitalize">{tx.type}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{tx.client_name}</TableCell>
                        <TableCell className={`text-right font-mono ${tx.type === 'payment' ? 'text-red-500' : 'text-green-500'}`}>
                          {tx.type === 'payment' ? '-' : '+'}{formatCurrency(tx.amount)}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(tx.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {allTransactions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No transactions found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Client Balances Tab */}
        <TabsContent value="balances" className="mt-6">
          <Card>
            <CardHeader>
              <SmartSearch
                data={allClients}
                columns={walletColumns}
                onFilter={setFilteredClients}
                quickFilters={walletQuickFilters}
                placeholder="Search by client name, code, type..."
              />
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Emirate</TableHead>
                    <TableHead className="text-right">Wallet Balance</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map(client => (
                    <TableRow key={client.id}>
                      <TableCell className="font-mono text-xs">{client.client_code}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {client.client_type === 'Company' ? (
                            <Building2 className="w-4 h-4 text-primary" />
                          ) : (
                            <User className="w-4 h-4 text-muted-foreground" />
                          )}
                          <span className="font-medium">{client.display_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{client.client_type}</Badge>
                      </TableCell>
                      <TableCell>{client.emirate}</TableCell>
                      <TableCell className="text-right">
                        <span className={`font-mono font-semibold ${(client.wallet_balance || 0) > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                          {formatCurrency(client.wallet_balance || 0)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate(`/clients/${client.id}`)}
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openTopupDialog(client)}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Top Up
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-4 text-sm text-muted-foreground">
                Showing {filteredClients.length} of {allClients.length} clients
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Transactions Tab */}
        <TabsContent value="transactions" className="mt-6">
          <Card>
            <CardHeader>
              <SmartSearch
                data={allTransactions}
                columns={transactionColumns}
                onFilter={setFilteredTransactions}
                quickFilters={transactionQuickFilters}
                placeholder="Search by transaction #, client, type, amount..."
              />
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Transaction #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Balance After</TableHead>
                    <TableHead>Reference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.map(tx => (
                    <TableRow key={tx.id || tx.transaction_number}>
                      <TableCell className="font-mono text-xs">{tx.transaction_number}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(tx.created_at)}</TableCell>
                      <TableCell className="font-medium">{tx.client_name}</TableCell>
                      <TableCell>{getTransactionTypeBadge(tx.type)}</TableCell>
                      <TableCell>{tx.payment_method || '-'}</TableCell>
                      <TableCell className={`text-right font-mono font-semibold ${tx.type === 'payment' ? 'text-red-500' : 'text-green-500'}`}>
                        {tx.type === 'payment' ? '-' : '+'}{formatCurrency(tx.amount)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(tx.balance_after)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {tx.reference_number || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredTransactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No transactions found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <div className="mt-4 text-sm text-muted-foreground">
                Showing {filteredTransactions.length} of {allTransactions.length} transactions
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Top-up History Tab */}
        <TabsContent value="topups" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Top-up History</CardTitle>
              <CardDescription>All wallet top-up transactions</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Transaction #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Payment Method</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allTransactions
                    .filter(tx => tx.type === 'topup')
                    .map(tx => (
                      <TableRow key={tx.id || tx.transaction_number}>
                        <TableCell className="font-mono text-xs">{tx.transaction_number}</TableCell>
                        <TableCell className="text-muted-foreground">{formatDate(tx.created_at)}</TableCell>
                        <TableCell className="font-medium">{tx.client_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{tx.payment_method}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold text-green-600">
                          +{formatCurrency(tx.amount)}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {tx.reference_number || '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">
                          {tx.notes || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  {allTransactions.filter(tx => tx.type === 'topup').length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No top-up transactions found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Low Balance Alerts Tab */}
        <TabsContent value="alerts" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Low Balance Alerts
              </CardTitle>
              <CardDescription>Clients with wallet balance below threshold</CardDescription>
            </CardHeader>
            <CardContent>
              {lowBalanceAlerts.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Current Balance</TableHead>
                      <TableHead className="text-right">Threshold</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lowBalanceAlerts.map(alert => (
                      <TableRow key={alert.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {alert.client_type === 'Company' ? (
                              <Building2 className="w-4 h-4 text-primary" />
                            ) : (
                              <User className="w-4 h-4 text-muted-foreground" />
                            )}
                            <span className="font-medium">{alert.display_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{alert.client_type}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-amber-600 font-semibold">
                          {formatCurrency(alert.wallet_balance || 0)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">
                          {formatCurrency(alert.threshold || 100)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={() => openTopupDialog(alert)}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Top Up Now
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">No Low Balance Alerts</p>
                  <p className="text-sm">All client wallets are above the threshold</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Top-up Dialog */}
      <Dialog open={topupDialogOpen} onOpenChange={setTopupDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" />
              Top Up Wallet
            </DialogTitle>
            <DialogDescription>
              {selectedClient 
                ? `Add funds to ${selectedClient.display_name}'s wallet`
                : 'Select a client and add funds to their wallet'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {!selectedClient && (
              <div>
                <Label>Select Client</Label>
                <Select onValueChange={(id) => setSelectedClient(allClients.find(c => c.id === id))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {allClients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.display_name} ({formatCurrency(client.wallet_balance || 0)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {selectedClient && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {selectedClient.client_type === 'Company' ? (
                      <Building2 className="w-4 h-4 text-primary" />
                    ) : (
                      <User className="w-4 h-4 text-muted-foreground" />
                    )}
                    <span className="font-medium">{selectedClient.display_name}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Current Balance</p>
                    <p className="font-mono font-semibold text-green-600">
                      {formatCurrency(selectedClient.wallet_balance || 0)}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (AED) *</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={topupForm.amount}
                  onChange={(e) => setTopupForm(prev => ({ ...prev, amount: e.target.value }))}
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select 
                  value={topupForm.payment_method} 
                  onValueChange={(v) => setTopupForm(prev => ({ ...prev, payment_method: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="Online">Online</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Reference Number</Label>
              <Input
                placeholder="Transaction reference (optional)"
                value={topupForm.reference_number}
                onChange={(e) => setTopupForm(prev => ({ ...prev, reference_number: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Input
                placeholder="Additional notes (optional)"
                value={topupForm.notes}
                onChange={(e) => setTopupForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
            
            {selectedClient && topupForm.amount && parseFloat(topupForm.amount) > 0 && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700">
                  New balance after top-up: <strong className="font-mono">
                    {formatCurrency((selectedClient.wallet_balance || 0) + parseFloat(topupForm.amount))}
                  </strong>
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setTopupDialogOpen(false);
              setSelectedClient(null);
              setTopupForm({ amount: '', payment_method: 'Cash', reference_number: '', notes: '' });
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleTopup} 
              disabled={saving || !selectedClient || !topupForm.amount}
            >
              {saving ? 'Processing...' : 'Confirm Top Up'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
